import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  DivideEntityProductTotalReceivedQuantityAmongOrdersBody,
  EntityProductApi,
  FilterEntityProductOptions,
  FindCollaboratorProductsType,
  FindEntityProductsForTableListQuery,
  FindEntityProductsToBalanceOptions,
  FindEntityProductsToOrderChainSupplierOptions,
  FindEntityProductsWithProductionPlanProductForecastToBalanceOptions,
  FindFreePartnerEntityProductsQuery,
  FindProductsForCreatePolicyQuery,
  FindProductsForReportQuantityType,
  FindProductsQuery,
  FindProductsWithExcludedProductsType,
  FindWithPartnerEntityProductsQuery,
  ImportEntityProductsFromGGSheet,
  RawEntityProduct,
  RecalculateTotalReceivedQuantityBody,
  ResetEntityProductTotalSupplierQuantityBody,
  UpdateEntityProductBody,
  UpdateMultiEntityProductsBody,
  ValidEntityProduct,
} from 'api';
import { product as productQueryInit } from 'containers/ProductionManager/ProductManager/Product/commons/query';

import { RootState } from '../app';

export const findProductsForTableList = createAsyncThunk(
  'entityProduct/findProductsForTableList',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForTableList(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsByEntitySubCategory = createAsyncThunk(
  'entityProduct/findProductsByEntitySubCategory',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsByEntitySubCategory(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneEntityProduct = createAsyncThunk(
  'entityProduct/findOneEntityProduct',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, option } = params;
      const response = await EntityProductApi.findOne(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneEntityProductWithID = createAsyncThunk(
  'entityProduct/findOneEntityProductWithID',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findOneWithID(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductsForTableList = createAsyncThunk(
  'entityProduct/findEntityProductsForTableList',
  async (option: FindEntityProductsForTableListQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findEntityProductsForTableList(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterEntityProductsForTableList = createAsyncThunk(
  'entityProduct/filterEntityProductsForTableList',
  async (option: FindEntityProductsForTableListQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.filterForTableList(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkExistEntityProducts = createAsyncThunk(
  'entityProduct/checkExistEntityProducts',
  async (
    params: { entityIDs: string[]; entityProductIDs: string[] },
    { rejectWithValue }
  ) => {
    const { entityIDs, entityProductIDs } = params;

    try {
      const response = await EntityProductApi.checkExistEntityProducts(
        entityIDs,
        entityProductIDs
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkExistEntityProductsOptimize = createAsyncThunk(
  'entityProduct/checkExistEntityProductsOptimize',
  async (
    params: { entityIDs: string[]; entityProductIDs: string[] },
    { rejectWithValue }
  ) => {
    const { entityIDs, entityProductIDs } = params;

    try {
      const response = await EntityProductApi.checkExistEntityProductsOptimize(
        entityIDs,
        entityProductIDs
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCollaboratorProductsForTableList = createAsyncThunk(
  'entityProduct/findForTableListWithExcludedProducts',
  async (option: FindCollaboratorProductsType, { rejectWithValue }) => {
    try {
      const response =
        await EntityProductApi.findCollaboratorProductsForTableList(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsHaveCoBuyPromotion = createAsyncThunk(
  'entityProduct/findProductsHaveCoBuyPromotion',
  async (option: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsHaveCoBuyPromotion(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findForTableListWithExcludedProducts = createAsyncThunk(
  'entityProduct/findForTableListWithExcludedProducts',
  async (option: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response =
        await EntityProductApi.findForTableListWithExcludedProducts(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForTableListWithExcludedProducts = createAsyncThunk(
  'entityProduct/findProductsForTableListWithExcludedProducts',
  async (option: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response =
        await EntityProductApi.findProductsForTableListWithExcludedProducts(
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForCoBuy = createAsyncThunk(
  'entityProduct/findProductsForCoBuy',
  async (params: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForCoBuy(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

// TODO: Need replace by filterEntityProduct
export const findProductsForSingleSelect = createAsyncThunk(
  'entityProduct/findProductsForSingleSelect',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForSingleSelect(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterEntityProduct = createAsyncThunk(
  'entityProduct/filterEntityProduct',
  async (
    params: { option: FilterEntityProductOptions; body?: { ninIDs: string[] } },
    { rejectWithValue }
  ) => {
    const { option, body } = params;

    try {
      const response = await EntityProductApi.filter(option, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsToConnect = createAsyncThunk(
  'entityProduct/findProductsToConnect',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsToConnect(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductForUpdate = createAsyncThunk(
  'entityProduct/findEntityProductForUpdate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findEntityProductForUpdate(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findFreeProductsForCombo = createAsyncThunk(
  'entityProduct/findFreeProductsForCombo',
  async (option: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findFreeProductsForCombo(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsToConnectNewEntity = createAsyncThunk(
  'entityProduct/findProductsToConnectNewEntity',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsToConnectNewEntity(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForMultiSelect = createAsyncThunk(
  'entityProduct/findProductsForMultiSelect',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForMultiSelect(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForSyncChain = createAsyncThunk(
  'entityProduct/findProductsForSyncChain',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForSyncChain(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForReportQuantity = createAsyncThunk(
  'entityProduct/findProductsForReportQuantity',
  async (params: FindProductsForReportQuantityType, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForReportQuantity(
        params
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForMarketing = createAsyncThunk(
  'entityProduct/findProductsForMarketing',
  async (option: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForMarketing(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForMarketingWithExcludedProducts = createAsyncThunk(
  'entityProduct/findProductsForMarketingWithExcludedProducts',
  async (option: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response =
        await EntityProductApi.findProductsForMarketingWithExcludedProducts(
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductsToOrderChainSupplier = createAsyncThunk(
  'entityProduct/findEntityProductsToOrderChainSupplier',
  async (
    option: FindEntityProductsToOrderChainSupplierOptions,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProductApi.findToOrderChainSupplier(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductsToBalance = createAsyncThunk(
  'entityProduct/findEntityProductsToBalance',
  async (option: FindEntityProductsToBalanceOptions, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findToBalance(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findWithProductionPlanProductForecastToBalance = createAsyncThunk(
  'entityProduct/findWithProductionPlanProductForecastToBalance',
  async (
    option: FindEntityProductsWithProductionPlanProductForecastToBalanceOptions,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await EntityProductApi.findWithProductionPlanProductForecastToBalance(
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForRetailDigitalMenu = createAsyncThunk(
  'entityProduct/findProductsForRetailDigitalMenu',
  async (params: FindProductsWithExcludedProductsType, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForRetailDigitalMenu(
        params
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForCreatePolicy = createAsyncThunk(
  'entityProduct/findProductsForCreatePolicy',
  async (query: FindProductsForCreatePolicyQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsForCreatePolicy(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);
export const findProductsForCreateAnonymousAccount = createAsyncThunk(
  'entityProduct/findProductsForCreateAnonymousAccount',
  async (query: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response =
        await EntityProductApi.findProductsForCreateAnonymousAccount(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsWithTotalStatistics = createAsyncThunk(
  'entityProduct/findProductsWithTotalStatistics',
  async (query: FindProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findProductsWithTotalStatistics(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findSelectedEntityProductsToCreatePolicy = createAsyncThunk(
  'product/findSelectedEntityProductsToCreatePolicy',
  async (
    params: { body: { productIDs: string[] }; option: any },
    { rejectWithValue }
  ) => {
    try {
      const { body, option } = params;
      const response =
        await EntityProductApi.findSelectedEntityProductsToCreatePolicy(
          body,
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductsWithPartnerEntityProducts = createAsyncThunk(
  'entityProduct/findEntityProductsWithPartnerEntityProducts',
  async (option: FindWithPartnerEntityProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findWithPartnerEntityProducts(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProductsWithPartnerEntityProductsForCollaborator =
  createAsyncThunk(
    'entityProduct/findEntityProductsWithPartnerEntityProductsForCollaborator',
    async (option: FindWithPartnerEntityProductsQuery, { rejectWithValue }) => {
      try {
        const response =
          await EntityProductApi.findWithPartnerEntityProductsForCollaborator(
            option
          );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findFreePartnerEntityProducts = createAsyncThunk(
  'entityProduct/findFreePartnerEntityProducts',
  async (option: FindFreePartnerEntityProductsQuery, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.findFreePartnerEntityProducts(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityProduct = createAsyncThunk(
  'entityProduct/updateEntityProduct',
  async (
    { id, body }: { id: string; body: UpdateEntityProductBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProductApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityProductTotalReceivedQuantity = createAsyncThunk(
  'entityProduct/updateEntityProductTotalReceivedQuantity',
  async (
    { id, options, body }: { id: string; options: any; body: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProductApi.updateTotalReceivedQuantity(
        id,
        options,
        body
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const divideEntityProductTotalReceivedQuantityAmongOrders =
  createAsyncThunk(
    'entityProduct/divideEntityProductTotalReceivedQuantityAmongOrders',
    async (
      {
        options,
        body,
      }: {
        options: any;
        body: DivideEntityProductTotalReceivedQuantityAmongOrdersBody;
      },
      { rejectWithValue }
    ) => {
      try {
        const response =
          await EntityProductApi.divideTotalReceivedQuantityAmongOrders(
            options,
            body
          );
        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const divideEntityProductTotalReceivedQuantityAmongOrdersOlder =
  createAsyncThunk(
    'entityProduct/divideEntityProductTotalReceivedQuantityAmongOrdersOlder',
    async (
      {
        options,
        body,
      }: {
        options: any;
        body: DivideEntityProductTotalReceivedQuantityAmongOrdersBody;
      },
      { rejectWithValue }
    ) => {
      try {
        const response =
          await EntityProductApi.divideTotalReceivedQuantityAmongOrdersOlder(
            options,
            body
          );
        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const deleteEntityProduct = createAsyncThunk(
  'entityProduct/deleteEntityProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.delete(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMultiEntityProducts = createAsyncThunk<
  any,
  { entityProductIDs: string[] },
  { rejectValue: { message: string } }
>(
  'entityProduct/deleteMultiEntityProducts',
  async (body, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.deleteMulti(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMultiEntityProducts = createAsyncThunk(
  'entityProduct/updateMultiEntityProducts',
  async (body: UpdateMultiEntityProductsBody, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.updateMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkCanDeleteEntityProduct = createAsyncThunk(
  'entityProduct/checkCanDeleteEntityProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.checkCanDelete(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkCanDeleteMultiEntityProducts = createAsyncThunk<
  any,
  { entityProductIDs: string[] },
  { rejectValue: { message: string } }
>(
  'entityProduct/checkCanDeleteMultiEntityProducts',
  async (body, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.checkCanDeleteMulti(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const getEntityProductsFromGGSheet = createAsyncThunk(
  'entityProduct/getEntityProductsFromGGSheet',
  async (query: ImportEntityProductsFromGGSheet, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.getDataFromGGSheet(query);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const getEntityProductsFromXLSX = createAsyncThunk(
  'entityProduct/getEntityProductsFromXLSX',
  async (body: RawEntityProduct[], { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.getDataFromXLSX(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const closeReuseDiaryProduct = createAsyncThunk(
  'entityProduct/closeReuseDiaryProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.closeReuseDiary(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const importEntityProductsFromGGSheet = createAsyncThunk(
  'entityProduct/importEntityProductsFromGGSheet',
  async (query: ImportEntityProductsFromGGSheet, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.import(query);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const importEntityProductsFromXLSX = createAsyncThunk(
  'entityProduct/importEntityProductsFromXLSX',
  async (body: ValidEntityProduct[], { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.importXLSX(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportEntityProducts = createAsyncThunk<
  any,
  { entityProductIDs: string[] },
  { rejectValue: { message: string } }
>('entityProduct/exportEntityProducts', async (body, { rejectWithValue }) => {
  try {
    const response = await EntityProductApi.exportEntityProducts(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const resetTotalReceivedQuantityForEntityProducts = createAsyncThunk(
  'entityProduct/resetTotalReceivedQuantityForEntityProducts',
  async (any, { rejectWithValue }) => {
    try {
      const response = await EntityProductApi.resetTotalReceivedQuantity();
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const resetTotalSupplierQuantityForEntityProducts = createAsyncThunk(
  'entityProduct/resetTotalSupplierQuantityForEntityProducts',
  async (
    {
      options,
      body,
    }: {
      options: any;
      body: ResetEntityProductTotalSupplierQuantityBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProductApi.resetTotalSupplierQuantity(
        options,
        body
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const resetTotalSupplierQuantityForEntityProductsOlder =
  createAsyncThunk(
    'entityProduct/resetTotalSupplierQuantityForEntityProductsOlder',
    async (
      {
        options,
        body,
      }: {
        options: any;
        body: ResetEntityProductTotalSupplierQuantityBody;
      },
      { rejectWithValue }
    ) => {
      try {
        const response = await EntityProductApi.resetTotalSupplierQuantityOlder(
          options,
          body
        );
        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const recalculateTotalReceivedQuantity = createAsyncThunk(
  'entityProduct/recalculateTotalReceivedQuantity',
  async (
    {
      options,
      body,
    }: {
      options: any;
      body: RecalculateTotalReceivedQuantityBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProductApi.recalculateTotalReceivedQuantity(
        options,
        body
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  tableList: Array<any>;
  orderList: Array<any>;
  list: Array<any>;
  reloadFlag: boolean;
  query: any;
  savedPagination?: any;
  savedList: Array<any>;
  selectedEntityProductIDsToExport: string[];
  finishExport: boolean;
}

const initialState: State = {
  tableList: [],
  orderList: [],
  list: [],
  reloadFlag: false,
  savedPagination: { current: 1, next: 2, prev: 0, take: 10, total: 0 },
  savedList: [],
  query: productQueryInit,
  selectedEntityProductIDsToExport: [],
  finishExport: false,
};

const entityProductSlice = createSlice({
  name: 'entityProduct',
  initialState,
  reducers: {
    saveSelectedEntityProductsIDsToExport(state, { payload }) {
      state.selectedEntityProductIDsToExport = payload;
      state.finishExport = false;
    },
    clearSelectedEntityProductsToExport(state, { payload }) {
      const { closeBottomActions } = payload;
      state.selectedEntityProductIDsToExport = [];
      if (closeBottomActions) {
        state.finishExport = true;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(findProductsForSingleSelect.fulfilled, (state, action) => {
      const { data } = action.payload;
      const newData = data.map(item => {
        return {
          ...item,
          key: item.id,
          value: item.id,
          infoBox: {
            name: item.name,
            code: item.code,
            avatar: item.avatar,
            productOptions: item.productOptions,
          },
        };
      });

      state.list = newData;
    });
  },
});

const { actions, reducer: entityProductReducer } = entityProductSlice;

export const {
  saveSelectedEntityProductsIDsToExport,
  clearSelectedEntityProductsToExport,
} = actions;

export default entityProductReducer;

export const selectEntityProduct = (state: RootState): State =>
  state.entityProduct as State;
